import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './ContractInfo.css';
import SectionHeader from './SectionHeader';
import { buildReferralDetailFields, hasReferralDetailContent } from './ReferralDetailsSection';
import { getInspectionTimelineApiUrl, getLoanTimelineApiUrl } from '../utils/apiUrls';

const LOAN_TIMELINE_URL = getLoanTimelineApiUrl();

const INSPECTION_TIMELINE_URL = getInspectionTimelineApiUrl();

const LISTING_ROLE_LABELS = {
  seller: 'Seller',
  co_seller: 'Co-Seller',
  sellers_agent: "Seller's Agent",
  listing_agent: 'Listing Agent',
  sellers_transaction_coordinator: "Seller's TC",
};

const fmtPct = (v) => v != null ? `${(v * 100).toFixed(2)}%` : null;
const fmtDollarCI = (v) => v != null ? `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 0 })}` : null;
const fmtBool = (v) => v == null ? null : v ? 'Yes' : 'No';

const ContractInfo = ({ contract, contractId, listing = null, isExpanded = false, onToggle }) => {
  const [loanTimeline, setLoanTimeline] = useState(null);
  const [loanTimelineLoading, setLoanTimelineLoading] = useState(false);
  const [loanTimelineError, setLoanTimelineError] = useState(null);
  const [inspectionTimeline, setInspectionTimeline] = useState(null);
  const [inspectionTimelineLoading, setInspectionTimelineLoading] = useState(false);
  const [inspectionTimelineError, setInspectionTimelineError] = useState(null);

  useEffect(() => {
    if (isExpanded && contractId) {
      fetchLoanTimeline();
      fetchInspectionTimeline();
    }
  }, [isExpanded, contractId]);

  const fetchLoanTimeline = async () => {
    try {
      setLoanTimelineLoading(true);
      setLoanTimelineError(null);
      const response = await axios.get(`${LOAN_TIMELINE_URL}/loan-timeline/${contractId}`);
      setLoanTimeline(response.data);
    } catch (err) {
      console.error('Error fetching loan timeline:', err);
      if (err.response?.status !== 404) {
        setLoanTimelineError('Failed to load loan timeline.');
      }
      // 404 is not an error - just means no loan timeline exists yet
    } finally {
      setLoanTimelineLoading(false);
    }
  };

  const fetchInspectionTimeline = async () => {
    try {
      setInspectionTimelineLoading(true);
      setInspectionTimelineError(null);
      const response = await axios.get(`${INSPECTION_TIMELINE_URL}/inspection-timeline/${contractId}`);
      setInspectionTimeline(response.data);
    } catch (err) {
      console.error('Error fetching inspection timeline:', err);
      if (err.response?.status !== 404) {
        setInspectionTimelineError('Failed to load inspection timeline.');
      }
      // 404 is not an error - just means no inspection timeline exists yet
    } finally {
      setInspectionTimelineLoading(false);
    }
  };
  // Display date strings as Jan. 1, 2026 format
  const formatDate = (dateStr) => {
    try {
      if (!dateStr) return 'N/A';

      let date;
      // Handle YYYY-MM-DD specifically to treat as local date (avoid timezone issues)
      const dateMatch = String(dateStr).match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (dateMatch) {
        const [_, y, m, d] = dateMatch;
        date = new Date(+y, +m - 1, +d);
      } else {
        date = new Date(dateStr);
      }

      if (isNaN(date.getTime())) return dateStr || 'N/A';

      // Format: "Jan 1, 2026" -> insert period for abbreviation matching user request "Jan. 1, 2026"
      const options = { month: 'short', day: 'numeric', year: 'numeric' };
      const formatted = date.toLocaleDateString('en-US', options);

      // Add period to 3-letter months (Jan, Feb, etc.) except May
      const parts = formatted.split(' ');
      if (parts.length >= 3 && parts[0].length === 3 && parts[0] !== 'May') {
        parts[0] += '.';
        return parts.join(' ');
      }
      return formatted;
    } catch (e) {
      return dateStr || 'N/A';
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return 'N/A';
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
      }).format(amount);
    } catch {
      return 'Invalid Amount';
    }
  };

  const formatBoolean = (value) => {
    if (value === null || value === undefined) return 'Not specified';
    return value ? 'Yes' : 'No';
  };

  /* Helper to format string values (capitalize and replace underscores) */
  const formatString = (val) => {
    if (!val) return 'N/A';
    if (typeof val !== 'string') return val; // Fallback for booleans/numbers if mixed
    return val.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const renderSection = (title, fields) => {
    // fields is array of [label, value, formatter?]
    // Filter out null/undefined values unless they are in the exception list
    const visibleFields = fields.filter(([label, value]) => {
      // Exception: Always show Title Officer, Title Officer Email, Title Company
      const alwaysShow = [
        'Title Company',
        'Title Officer',
        'Title Officer Email'
      ].includes(label);

      if (alwaysShow) return true;

      // Otherwise hide if null, undefined, or empty string
      return value !== null && value !== undefined && value !== '';
    });

    if (visibleFields.length === 0) return null;

    return (
      <div className="info-section">
        <h3>{title}</h3>
        <div className="info-grid">
          {visibleFields.map(([label, value, formatter = (v) => v]) => (
            <div key={label} className="info-item">
              <span className="info-label">{label}:</span>
              <span className="info-value">{formatter(value)}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderInspectionSection = (fields, repairs) => {
    // Same filtering logic for inspection section
    const visibleFields = fields.filter(([label, value]) => {
      return value !== null && value !== undefined && value !== '';
    });

    if (visibleFields.length === 0 && (!repairs || repairs.length === 0)) return null;

    return (
      <div className="info-section">
        <h3>Inspection</h3>
        <div className="info-grid">
          {visibleFields.map(([label, value, formatter = (v) => v]) => (
            <div key={label} className="info-item">
              <span className="info-label">{label}:</span>
              <span className="info-value">{formatter(value)}</span>
            </div>
          ))}
        </div>

        {repairs && repairs.length > 0 && (
          <div className="inspection-repairs">
            <div className="inspection-repairs-title">Requested Repairs</div>
            <div className="inspection-repairs-list">
              {repairs
                .map((r) => (r?.repair_description || '').trim())
                .filter(Boolean)
                .map((desc, idx) => (
                  <div key={`${idx}-${desc}`} className="inspection-repair-chip">
                    {desc}
                  </div>
                ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  if (!contract) {
    return <div>No contract data available</div>;
  }

  return (
    <div className="contract-info">
      <SectionHeader
        title="Contract Details"
        isExpanded={isExpanded}
        onToggle={onToggle}
      />


      {isExpanded && (
        <div className="contract-info-container">

          {/* Financial Information */}
          {renderSection('Financial Information', [
            ['Purchase Price', contract.purchase_price, formatCurrency],
            ['Earnest Money Deposit', contract.earnest_money_deposit_amount, formatCurrency],
            ['Earnest Money Days', contract.earnest_money_deposit_days],
            ['Additional Deposit', contract.additional_deposit, formatBoolean],
            ['Additional Deposit Amount', contract.additional_deposit_amount, formatCurrency],
            ['Expected Loan Amount', contract.expected_loan_amount, formatCurrency],
            ['Cash Balance Remaining', contract.cash_balance_for_buyer_remaining, formatCurrency]
          ])}

          {/* Title Company Information */}
          {renderSection('Title Company Information', [
            ['Title Company', contract.title_company_name],
            ['Title Officer', contract.title_officer_name],
            ['Title Officer Email', contract.title_officer_email]
          ])}

          {/* Loan Contingency */}
          {renderSection('Loan Contingency', [
            ['Loan Contingency', contract.loan_contingency, formatBoolean],
            ['Loan Contingency Days', contract.loan_contingency_days],
            ['Minimum Loan Amount', contract.loan_contingency_amount_minimum, formatCurrency],
            ['Maximum Interest Rate', contract.loan_contingency_interest_rate_maximum],
            ['Expected Loan Term', contract.loan_term_expected, formatString],
            ['Expected Loan Type', contract.loan_type_expected, formatString],
            // Loan Timeline fields
            ...(loanTimeline ? [
              ['Time Reference Date', loanTimeline.time_reference_date, formatDate],
              ['Escrow Closing Date', loanTimeline.escrow_closing_date, formatDate],
              ['Buyer Loan Terms Rejection Date', loanTimeline.buyer_initiated_loan_terms_rejection_date, formatDate],
              ['Loan Application Deadline', loanTimeline.loan_application_deadline, formatDate],
              ['Loan Application Status', loanTimeline.loan_application_status, formatString],
              ['Conditional Approval Deadline', loanTimeline.loan_conditional_approval_deadline, formatDate],
              ['Conditional Approval Status', loanTimeline.loan_conditional_approval_status, formatString],
              ['Seller Cancellation Right Start Date', loanTimeline.seller_cancellation_right_start_date, formatDate],
              ['Seller Cancellation Right Status', loanTimeline.seller_cancellation_right_status, formatString],
              ['Appraisal Scheduled Date', loanTimeline.property_appraisal_scheduled_date, formatDate],
              ['Appraisal Received Date', loanTimeline.property_appraisal_received_by_buyer_date, formatDate],
              ['Appraisal Status', loanTimeline.property_appraisal_status, formatString],
              ['Appraised Value Cancellation Deadline', loanTimeline.appraised_value_cancellation_deadline, formatDate],
              ['Final Loan Contingency Deadline', loanTimeline.final_loan_contingency_notice_deadline, formatDate]
            ] : [])
          ])}

          {/* Inspection */}
          {renderInspectionSection([
            ['Inspection Contingency', contract.inspection_contingency, formatBoolean],
            ['Inspection Contingency Days', contract.inspection_contingency_days],
            ['Inspection Contingency Due Date', contract.inspection_contingency_due_date, formatDate],
            ['Inspection Report Due to Seller', contract.inspection_report_due_to_seller, formatDate],
            // Inspection Timeline fields
            ...(inspectionTimeline?.inspection_timeline ? [
              ['Time Reference Date', inspectionTimeline.inspection_timeline.time_reference_date, formatDate],
              ['Escrow Closing Date', inspectionTimeline.inspection_timeline.escrow_closing_date, formatDate],
              ['Inspection Report Due to Seller', inspectionTimeline.inspection_timeline.inspection_report_due_to_seller, formatDate],
              ['TRR Submitted to Seller Date', inspectionTimeline.inspection_timeline.trr_submitted_to_seller_date, formatDate],
              ['TRR Seller Response Date', inspectionTimeline.inspection_timeline.trr_seller_response_date, formatDate],
              ['TRR Buyer Response Date', inspectionTimeline.inspection_timeline.trr_buyer_response_date, formatDate],
              ['Buyer Accepts Property As-Is Date', inspectionTimeline.inspection_timeline.buyer_accepts_property_as_is_date, formatDate],
              ['General Inspection Scheduled Date', inspectionTimeline.inspection_timeline.general_inspection_scheduled_date, formatDate],
              ['General Inspection Vendor', inspectionTimeline.inspection_timeline.general_inspection_vendor_name],
              ['General Inspector Name', inspectionTimeline.inspection_timeline.general_inspector_name],
              ['General Inspector Email', inspectionTimeline.inspection_timeline.general_inspector_email],
              ['Pest Inspection Scheduled Date', inspectionTimeline.inspection_timeline.pest_inspection_scheduled_date, formatDate],
              ['Pest Inspection Vendor', inspectionTimeline.inspection_timeline.pest_inspection_vendor_name],
              ['Pest Inspector Name', inspectionTimeline.inspection_timeline.pest_inspector_name],
              ['Pest Inspector Email', inspectionTimeline.inspection_timeline.pest_inspector_email],
              ['General Inspection Report Received Date', inspectionTimeline.inspection_timeline.general_inspection_report_received_date, formatDate],
              ['Pest Inspection Report Received Date', inspectionTimeline.inspection_timeline.pest_inspection_report_received_date, formatDate],
              ['General Inspection Report Provided to Seller Date', inspectionTimeline.inspection_timeline.general_inspection_report_provided_to_seller_date, formatDate],
              ['Pest Inspection Report Provided to Seller Date', inspectionTimeline.inspection_timeline.pest_inspection_report_provided_to_seller_date, formatDate],
              ['TRR Negotiation End Date', inspectionTimeline.inspection_timeline.trr_negotiation_end_date, formatDate],
              ['TRR Agreement Status', inspectionTimeline.inspection_timeline.trr_agreement_status, formatString],
              ['Buyer TRR Decision Status', inspectionTimeline.inspection_timeline.buyer_trr_decision_status, formatString]
            ] : []),
          ], inspectionTimeline?.repairs)}

          {/* Contingencies */}
          {renderSection('Contingencies', [
            ['Appraisal Contingency', contract.appraisal_contingency, formatBoolean],
            ['Appraisal Contingency Days', contract.appraisal_contingency_days],
            ['Buyer Property Sale Contingency', contract.buyer_property_sale_contingency, formatBoolean],
            ['Buyer Property Sale Days', contract.buyer_property_sale_contingency_days],
            ['Title Review Contingency', contract.title_review_contingency, formatBoolean],
            ['Title Review Days', contract.title_review_contingency_days],
            ['Insurance Contingency', contract.insurance_contingency, formatBoolean],
            ['Insurance Contingency Days', contract.insurance_contingency_days],
            ['Seller Disclosures Contingency', contract.seller_disclosures_contingency, formatBoolean],
            ['Seller Disclosures Days', contract.seller_disclosures_contingency_days],
            ['HOA Contingency', contract.hoa_contingency, formatBoolean],
            ['HOA Contingency Days', contract.hoa_contingency_days],
            ['Rental Contingency', contract.rental_contingency, formatBoolean],
            ['Rental Contingency Days', contract.rental_contingency_days],
            ['Rental Property Accounting Contingency', contract.rental_property_accounting_contingency, formatBoolean],
            ['Rental Property Accounting Days', contract.rental_property_accounting_contingency_days]
          ])}

          {/* 1031 Exchange */}
          {renderSection('1031 Exchange', [
            ['Buyer 1031 Exchange', contract.buyer_1031_exchange, formatBoolean],
            ['Seller 1031 Exchange', contract.seller_1031_exchange, formatBoolean]
          ])}

          {/* Addenda Notes */}
          {contract.counteroffer_addenda_notes && contract.counteroffer_addenda_notes.length > 0 && (
            <div className="info-section">
              <h3>Addenda Notes</h3>
              <ul className="addenda-notes-list">
                {contract.counteroffer_addenda_notes.map((note, idx) => (
                  <li key={idx} className="addenda-notes-item">{note}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Listing Agreement Details — shown when this contract originated from a listing */}
          {listing && (() => {
            const tcFee = listing.sellers_tc_fee_amount
              ? `${fmtDollarCI(listing.sellers_tc_fee_amount)} (paid by ${listing.sellers_tc_fee_payer || '?'})`
              : null;
            return (
              <>
                {renderSection('Listing Agreement', [
                  ['MLS Number', listing.mls_number],
                  ['Listing Date', listing.listing_date],
                  ['Expiration Date', listing.expiration_date],
                  ['Listing Price', listing.listing_price, fmtDollarCI],
                  listing.commission_percent != null
                    ? ['Listing Commission', listing.commission_percent, fmtPct]
                    : ['Listing Commission', listing.commission_amount, fmtDollarCI],
                  ["Seller's Agent Commission", listing.cooperative_commission_percent_sellers_agent, fmtPct],
                  ["Buyer's Agent Commission", listing.cooperative_commission_percent_buyers_agent, fmtPct],
                  ["Seller's TC Fee", tcFee],
                  ['Lock Box Allowed', listing.lock_box_allowed, fmtBool],
                  ['Home Warranty Offered', listing.home_warranty_offered, fmtBool],
                  ['Included Items', listing.included_items],
                  ['Excluded Items', listing.excluded_items],
                ])}
                {listing.parties && listing.parties.length > 0 && (
                  <div className="info-section">
                    <h3>Listing Parties</h3>
                    <div className="info-grid">
                      {listing.parties.map(p => (
                        <div key={p.id} className="info-item">
                          <span className="info-label">{LISTING_ROLE_LABELS[p.role] || p.role}:</span>
                          <span className="info-value">{[p.name, p.email].filter(Boolean).join(' — ') || '—'}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            );
          })()}

          {/* Commission and Fees */}
          {renderSection('Commission and Fees', [
            ['Buyer Agent Commission Payer', contract.buyer_agent_commission_payer, formatString],
            // Buyer agent commission amount: show percent or fixed, then calculated if available
            ...(contract.commission_details?.buyer_agent_commission_percent != null
              ? [['Buyer Agent Commission', `${(contract.commission_details.buyer_agent_commission_percent * 100).toFixed(2)}%`]]
              : contract.commission_details?.buyer_agent_commission_amount != null
              ? [['Buyer Agent Commission', formatCurrency(contract.commission_details.buyer_agent_commission_amount)]]
              : []),
            ...(contract.commission_details?.buyer_agent_commission_calculated_amount != null
              ? [['Buyer Agent Commission (Calculated)', formatCurrency(contract.commission_details.buyer_agent_commission_calculated_amount)]]
              : []),
            ['Seller Agent Commission Payer', contract.seller_agent_commission_payer, formatString],
            ...(contract.commission_details?.seller_agent_commission_percent != null
              ? [['Seller Agent Commission', `${(contract.commission_details.seller_agent_commission_percent * 100).toFixed(2)}%`]]
              : contract.commission_details?.seller_agent_commission_amount != null
              ? [['Seller Agent Commission', formatCurrency(contract.commission_details.seller_agent_commission_amount)]]
              : []),
            ...(contract.commission_details?.seller_agent_commission_calculated_amount != null
              ? [['Seller Agent Commission (Calculated)', formatCurrency(contract.commission_details.seller_agent_commission_calculated_amount)]]
              : []),
            ['Transfer Tax Payer', contract.transfer_tax_payer, formatString],
            ['Escrow Fees Payer', contract.escrow_fees_payer, formatString],
            ['Title Insurance Premium Payer', contract.title_insurance_premium_payer, formatString]
          ])}

          {hasReferralDetailContent(contract.seller_referral_details) &&
            renderSection('Referral Details (Seller)', buildReferralDetailFields(contract.seller_referral_details))}
          {hasReferralDetailContent(contract.buyer_referral_details) &&
            renderSection('Referral Details (Buyer)', buildReferralDetailFields(contract.buyer_referral_details))}
          {!hasReferralDetailContent(contract.seller_referral_details) &&
            !hasReferralDetailContent(contract.buyer_referral_details) &&
            hasReferralDetailContent(contract.referral_details) &&
            renderSection('Referral Details', buildReferralDetailFields(contract.referral_details))}

        </div>
      )}
    </div>
  );
};

export default ContractInfo;
